import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, onBeforeUnmount } from 'vue';
import SvgIcon from '../../common/base/SvgIcon.vue';
import LoadingIcon from '../../common/icons/LoadingIcon.vue';
import NextIcon from '../../common/icons/NextIcon.vue';
import TuiButton from '../../common/base/Button.vue';
import Logo from '../../common/Logo.vue';
import useRoomControl from './useRoomControlHooks';
import CreateRoomIcon from '../../common/icons/CreateRoomIcon.vue';
import EnterRoomIcon from '../../common/icons/EnterRoomIcon.vue';
import useGetRoomEngine from '../../../hooks/useRoomEngine';
import { useBasicStore } from '../../../stores/basic';
import { useRoomStore } from '../../../stores/room';
import AudioMediaControl from '../../common/AudioMediaControl.vue';
import VideoMediaControl from '../../common/VideoMediaControl.vue';
import TUIRoomEngine, { TUIRoomEvents, TRTCDeviceType, TRTCVideoMirrorType, TRTCVideoRotation, TRTCDeviceState, TRTCVideoFillMode } from '@tencentcloud/tuiroom-engine-js';
import '../../../directives/vClickOutside';
import logger from '../../../utils/common/logger';
import { isElectron } from '../../../utils/environment';
import TUIMessageBox from '../../common/base/MessageBox/index';
import { isEnumerateDevicesSupported, isGetUserMediaSupported } from '../../../utils/mediaAbility';


export default /*#__PURE__*/_defineComponent({
  __name: 'RoomControlPC',
  props: {
    showLogo: { type: Boolean, default: true },
    givenRoomId: null
  },
  emits: ['create-room', 'enter-room'],
  setup(__props: any, { expose, emit }) {

const props = __props as { showLogo: boolean, givenRoomId: string | null }

const roomStore = useRoomStore();
const basicStore = useBasicStore();
const {
  t,
} = useRoomControl();



expose({
  getRoomParam,
  startStreamPreview,
});

const roomEngine = useGetRoomEngine();
const isCameraLoading = ref(false);

const audioVolume = ref(0);
const isMicMuted = ref(false);
const isCameraMuted = ref(false);

const showCreateRoomItems = ref(false);
const showEnterRoomAction = ref(Boolean(props.givenRoomId));

const tuiRoomParam = {
  isOpenCamera: true,
  isOpenMicrophone: true,
  defaultCameraId: '',
  defaultMicrophoneId: '',
  defaultSpeakerId: '',
};

async function openCamera() {
  const trtcCloud = roomEngine.instance?.getTRTCCloud();
  await trtcCloud?.setLocalRenderParams({
    mirrorType: basicStore.isLocalStreamMirror
      ? TRTCVideoMirrorType.TRTCVideoMirrorType_Enable : TRTCVideoMirrorType.TRTCVideoMirrorType_Disable,
    rotation: TRTCVideoRotation.TRTCVideoRotation0,
    fillMode: TRTCVideoFillMode.TRTCVideoFillMode_Fill,
  });
  if (isElectron) {
    roomEngine.instance?.setLocalVideoView({ view: 'stream-preview' });
    await roomEngine.instance?.openLocalCamera();
  } else {
    await roomEngine.instance?.startCameraDeviceTest({
      view: 'stream-preview',
    });
  }
}

async function closeCamera() {
  if (isElectron) {
    await roomEngine.instance?.closeLocalCamera();
  } else {
    await roomEngine.instance?.stopCameraDeviceTest();
  }
}

async function openAudio() {
  if (isElectron) {
    await roomEngine.instance?.openLocalMicrophone();
  } else {
    await roomEngine.instance?.startMicDeviceTest({ interval: 200 });
  }
}

async function closeAudio() {
  if (isElectron) {
    await roomEngine.instance?.closeLocalMicrophone();
  } else {
    await roomEngine.instance?.stopMicDeviceTest();
  }
}

async function toggleMuteAudio() {
  isMicMuted.value = !isMicMuted.value;
  tuiRoomParam.isOpenMicrophone = !isMicMuted.value;
  if (isMicMuted.value) {
    await closeAudio();
    audioVolume.value = 0;
  } else {
    await openAudio();
  }
}

async function toggleMuteVideo() {
  isCameraMuted.value = !isCameraMuted.value;
  tuiRoomParam.isOpenCamera = !isCameraMuted.value;
  if (isCameraMuted.value) {
    await closeCamera();
  } else {
    isCameraLoading.value = true;
    await openCamera();
    isCameraLoading.value = false;
  }
}

function getRoomParam() {
  tuiRoomParam.defaultCameraId = roomStore.currentCameraId;
  tuiRoomParam.defaultMicrophoneId = roomStore.currentMicrophoneId;
  tuiRoomParam.defaultSpeakerId = roomStore.currentSpeakerId;
  return tuiRoomParam;
}

const onUserVoiceVolume = (result: any) => {
  audioVolume.value = result;
};

async function startStreamPreview() {
  if (!isEnumerateDevicesSupported) {
    return;
  }
  isCameraLoading.value = true;
  const cameraList = await roomEngine.instance?.getCameraDevicesList();
  const microphoneList = await roomEngine.instance?.getMicDevicesList();
  const speakerList = await roomEngine.instance?.getSpeakerDevicesList();

  const hasCameraDevice = cameraList && cameraList.length > 0;
  const hasMicrophoneDevice = microphoneList && microphoneList.length > 0;
  let alertMessage = '';
  if (hasCameraDevice && !hasMicrophoneDevice) {
    alertMessage = 'Microphone not detected on current device';
  } else if (!hasCameraDevice && hasMicrophoneDevice) {
    alertMessage = 'Camera not detected on current device';
  } else if (!hasCameraDevice && !hasMicrophoneDevice) {
    alertMessage = 'Camera And Microphone not detected on current device';
  }
  if (alertMessage) {
    TUIMessageBox({
      title: t('Note'),
      message: t(alertMessage),
      appendToRoomContainer: true,
      confirmButtonText: t('Sure'),
    });
  }

  cameraList && roomStore.setCameraList(cameraList);
  microphoneList && roomStore.setMicrophoneList(microphoneList);
  speakerList && roomStore.setSpeakerList(speakerList);
  const cameraInfo = roomEngine.instance?.getCurrentCameraDevice();
  const micInfo = roomEngine.instance?.getCurrentMicDevice();
  const speakerInfo = roomEngine.instance?.getCurrentSpeakerDevice();
  if (cameraInfo && cameraInfo.deviceId) {
    roomStore.setCurrentCameraId(cameraInfo.deviceId);
  }
  if (micInfo && micInfo.deviceId) {
    roomStore.setCurrentMicrophoneId(micInfo.deviceId);
  }
  if (speakerInfo && speakerInfo.deviceId) {
    roomStore.setCurrentSpeakerId(speakerInfo.deviceId);
  }

  if (!isGetUserMediaSupported) {
    isCameraLoading.value = false;
    return;
  }
  hasMicrophoneDevice && openAudio();
  hasCameraDevice && await openCamera();
  isCameraLoading.value = false;
}

const roomId = ref(props.givenRoomId);

watch(() => props.givenRoomId, (val) => {
  roomId.value = val;
  showEnterRoomAction.value = Boolean(val);
}, { immediate: true });



function handleCreateRoom() {
  showCreateRoomItems.value = !showCreateRoomItems.value;
}

function handleClickOutsideCreateRoomItems() {
  if (showCreateRoomItems.value) {
    showCreateRoomItems.value = false;
  }
}

function handleEnterRoom() {
  showEnterRoomAction.value = true;
}

function createRoom(mode: string) {
  emit('create-room', mode);
}

function enterRoom() {
  if (!roomId.value) {
    return;
  }
  emit('enter-room', String(roomId.value));
}

/**
 * Device changes: device switching, device plugging and unplugging events
 *
 * 设备变化：设备切换、设备插拔事件
 **/
async function onDeviceChange(eventInfo: {deviceId: string, type: number, state: number}) {
  const stateList = ['add', 'remove', 'active'];
  const { deviceId, type, state } = eventInfo;
  if (type === TRTCDeviceType.TRTCDeviceTypeMic) {
    logger.log(`onDeviceChange: deviceId: ${deviceId}, type: microphone, state: ${stateList[state]}`);
    const deviceList = await roomEngine.instance?.getMicDevicesList();
    roomStore.setMicrophoneList(deviceList);
    if (state === TRTCDeviceState.TRTCDeviceStateActive) {
      roomStore.setCurrentMicrophoneId(deviceId);
    }
    return;
  }
  if (type === TRTCDeviceType.TRTCDeviceTypeSpeaker) {
    logger.log(`onDeviceChange: deviceId: ${deviceId}, type: speaker, state: ${stateList[state]}`);
    const deviceList = await roomEngine.instance?.getSpeakerDevicesList();
    roomStore.setSpeakerList(deviceList);
    if (state === TRTCDeviceState.TRTCDeviceStateActive) {
      roomStore.setCurrentSpeakerId(deviceId);
    }
    return;
  }
  if (type === TRTCDeviceType.TRTCDeviceTypeCamera) {
    logger.log(`onDeviceChange: deviceId: ${deviceId}, type: camera, state: ${stateList[state]}`);
    const deviceList = await roomEngine.instance?.getCameraDevicesList();
    roomStore.setCameraList(deviceList);
    if (state === TRTCDeviceState.TRTCDeviceStateActive) {
      roomStore.setCurrentCameraId(deviceId);
    }
  }
}

TUIRoomEngine.once('ready', () => {
  startStreamPreview();

  if (isElectron) {
    roomEngine.instance?.on(TUIRoomEvents.onUserVoiceVolumeChanged, onUserVoiceVolume);
  } else {
    // 兼容没有打开音频前，roomEngine 没有抛出音量事件的问题
    const trtcCloud = roomEngine.instance?.getTRTCCloud();
    trtcCloud?.on('onTestMicVolume', onUserVoiceVolume);
  }
  roomEngine.instance?.on(TUIRoomEvents.onDeviceChange, onDeviceChange);
});

onBeforeUnmount(async () => {
  await closeAudio();
  await closeCamera();

  if (isElectron) {
    roomEngine.instance?.off(TUIRoomEvents.onUserVoiceVolumeChanged, onUserVoiceVolume);
  } else {
    const trtcCloud = roomEngine.instance?.getTRTCCloud();
    trtcCloud?.off('onTestMicVolume', onUserVoiceVolume);
  }
  roomEngine.instance?.off(TUIRoomEvents.onDeviceChange, onDeviceChange);
});

return { __sfc: true,roomStore, basicStore, t, props, roomEngine, isCameraLoading, audioVolume, isMicMuted, isCameraMuted, showCreateRoomItems, showEnterRoomAction, tuiRoomParam, openCamera, closeCamera, openAudio, closeAudio, toggleMuteAudio, toggleMuteVideo, getRoomParam, onUserVoiceVolume, startStreamPreview, roomId, emit, handleCreateRoom, handleClickOutsideCreateRoomItems, handleEnterRoom, createRoom, enterRoom, onDeviceChange, SvgIcon, LoadingIcon, Logo, EnterRoomIcon, AudioMediaControl, VideoMediaControl }
}

})